import {
  Zap,
  ScanSearch,
  Cable,
  Scale,
  Shuffle,
  Lightbulb,
  ShieldCheck,
  RefreshCw,
  ChevronRight,
} from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SectionShell, SectionHead, IconChip } from "@/components/site/Primitives";

const STEPS = [
  { icon: ScanSearch, tint: "blue", n: 1, title: "Detect", copy: "Identify the event, affected assets, contracts, parties and decisions requiring attention." },
  { icon: Cable, tint: "blue", n: 2, title: "Connect & Contextualise", copy: "Bring together relevant contractual, operational, financial, market and external information." },
  { icon: Scale, tint: "gold", n: 3, title: "Reason & Quantify", copy: "Evaluate obligations, dependencies, commercial exposure and financial impact." },
  { icon: Shuffle, tint: "gold", n: 4, title: "Simulate Options", copy: "Compare alternative actions, trade-offs, probabilities and possible outcomes." },
  { icon: Lightbulb, tint: "gold", n: 5, title: "Recommend Action", copy: "Generate a prioritised recommendation with rationale, confidence and expected impact." },
  { icon: ShieldCheck, tint: "purple", n: 6, title: "Verify, Approve & Act", copy: "Review the evidence, assumptions and limitations before an authorised person approves, modifies, rejects or escalates the action." },
  { icon: RefreshCw, tint: "purple", n: 7, title: "Learn & Improve", copy: "Validate the commercial outcome and strengthen Customer-Specific Decision Memory™ for future recommendations." },
];

const ALIGNMENT = [
  { state: "Detect", steps: "Steps 1–2" },
  { state: "Decide", steps: "Steps 3–5" },
  { state: "Verify", steps: "Step 6" },
  { state: "Learning", steps: "Step 7" },
];

export function Workflow() {
  return (
    <SectionShell id="workflow" tone="mist">
      <div className="max-w-3xl">
        <SectionHead
          label="How It Works"
          labelColor="gold"
          title="From commercial event to executive decision."
          intro="A commercial event triggers a seven-step governed decision workflow — keeping authorised people in control at the point of action."
        />
      </div>

      {/* Trigger */}
      <Reveal>
        <div className="mt-14 flex flex-col items-stretch gap-4 lg:flex-row lg:items-center">
          <div className="flex items-start gap-3.5 rounded-2xl border border-natsu-amber/30 bg-natsu-amber/[0.05] p-5 lg:max-w-sm">
            <IconChip icon={Zap} tint="gold" />
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-natsu-amber">Trigger</p>
              <h3 className="font-display text-lg text-natsu-navy">Commercial Event</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-natsu-navy/70">
                A disruption, voyage issue, contract change, claim, market shift,
                compliance alert or counterparty event occurs.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-center lg:px-1">
            <ChevronRight className="hidden h-6 w-6 text-natsu-slate/40 lg:block" />
          </div>
          <p className="hidden text-[13px] font-medium text-natsu-slate lg:block">
            Initiates the seven governed steps →
          </p>
        </div>
      </Reveal>

      {/* Desktop horizontal stepper */}
      <Reveal>
        <div className="mt-10 hidden xl:grid xl:grid-cols-7 xl:gap-4">
          {STEPS.map((s) => (
            <div key={s.n} className="relative flex flex-col rounded-2xl border border-natsu-navy/[0.08] bg-white p-4">
              <div className="flex items-center justify-between">
                <IconChip icon={s.icon} tint={s.tint} />
                <span className="font-display text-sm text-natsu-slate/50">0{s.n}</span>
              </div>
              <h3 className="mt-4 font-display text-[15px] leading-snug text-natsu-navy">{s.title}</h3>
              <p className="mt-2 text-[12px] leading-relaxed text-natsu-slate">{s.copy}</p>
            </div>
          ))}
        </div>
      </Reveal>

      {/* Tablet grid */}
      <div className="mt-10 hidden gap-4 sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:hidden">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={(i % 3) * 0.06}>
            <div className="flex h-full flex-col rounded-2xl border border-natsu-navy/[0.08] bg-white p-5">
              <div className="flex items-center justify-between">
                <IconChip icon={s.icon} tint={s.tint} />
                <span className="font-display text-sm text-natsu-slate/50">0{s.n}</span>
              </div>
              <h3 className="mt-4 font-display text-lg leading-snug text-natsu-navy">{s.title}</h3>
              <p className="mt-2 text-[13px] leading-relaxed text-natsu-slate">{s.copy}</p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Mobile vertical timeline */}
      <div className="mt-10 sm:hidden">
        <ol className="relative border-l border-natsu-navy/12 pl-6">
          {STEPS.map((s) => (
            <li key={s.n} className="relative mb-7 last:mb-0">
              <span className="absolute -left-[33px] flex h-6 w-6 items-center justify-center rounded-full bg-natsu-navy text-[11px] font-semibold text-natsu-warm-white">
                {s.n}
              </span>
              <h3 className="font-display text-lg leading-snug text-natsu-navy">{s.title}</h3>
              <p className="mt-1.5 text-[13.5px] leading-relaxed text-natsu-slate">{s.copy}</p>
            </li>
          ))}
        </ol>
      </div>

      {/* Alignment to product states */}
      <Reveal>
        <div className="mt-12 rounded-2xl border border-natsu-navy/[0.08] bg-white p-6">
          <p className="eyebrow text-natsu-slate">How the product experience maps to the workflow</p>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {ALIGNMENT.map((a) => (
              <div key={a.state} className="rounded-xl bg-natsu-mist px-4 py-3">
                <p className="font-display text-base text-natsu-navy">{a.state}</p>
                <p className="mt-1 text-[12.5px] text-natsu-slate">{a.steps}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </SectionShell>
  );
}
